import { database, KnowledgeBase, ProductInfo, SectorInfo } from '../../data/database';

// Interface for vector search result
interface VectorSearchResult {
  content: string;
  score: number;
}

// Simplified vector store service that doesn't rely on OpenAI embeddings
export class VectorStoreService {
  private contents: Map<string, string> = new Map();
  private initialized = false;

  constructor() {
    this.initializeVectorStore();
  }

  // Initialize the vector store with product and sector information
  private initializeVectorStore() {
    try {
      this.addProductsToVectorStore(database.products);
      this.addSectorsToVectorStore(database.sectors);
      this.addGeneralInfoToVectorStore(database.generalInfo);
      this.initialized = true;
      console.log('Vector store initialized successfully');
    } catch (error) {
      console.error('Failed to initialize vector store:', error);
    }
  }

  // Add products to the vector store
  private addProductsToVectorStore(products: ProductInfo[]) {
    for (const product of products) {
      const productContent = this.formatProductContent(product);
      this.addToVectorStore(`product-${product.id}`, productContent);
    }
  }

  // Add sectors to the vector store
  private addSectorsToVectorStore(sectors: SectorInfo[]) {
    for (const sector of sectors) {
      const sectorContent = this.formatSectorContent(sector);
      this.addToVectorStore(`sector-${sector.id}`, sectorContent);
    }
  }

  // Add general company information to the vector store
  private addGeneralInfoToVectorStore(generalInfo: KnowledgeBase['generalInfo']) {
    const generalInfoContent = this.formatGeneralInfoContent(generalInfo);
    this.addToVectorStore('general-info', generalInfoContent);
  }

  // Format product content for storage
  private formatProductContent(product: ProductInfo): string {
    return `
      Product: ${product.name}
      Description: ${product.description}
      Features: ${product.features.join(', ')}
      Applications: ${product.applications.join(', ')}
      ${product.specifications ? `Specifications: ${JSON.stringify(product.specifications)}` : ''}
    `;
  }

  // Format sector content for storage
  private formatSectorContent(sector: SectorInfo): string {
    return `
      Sector: ${sector.name}
      Description: ${sector.description}
      Applications: ${sector.applications.join(', ')}
      Benefits: ${sector.benefits.join(', ')}
    `;
  }

  // Format general info content for storage
  private formatGeneralInfoContent(generalInfo: KnowledgeBase['generalInfo']): string {
    return `
      Company Description: ${generalInfo.companyDescription}
      Unique Selling Points: ${generalInfo.usp.join(', ')}
      Values: ${generalInfo.values.join(', ')}
    `;
  }

  // Add content to vector store
  private addToVectorStore(id: string, content: string) {
    try {
      this.contents.set(id, content);
    } catch (error) {
      console.error(`Failed to add content to vector store: ${id}`, error);
    }
  }

  // Simple keyword-based search as a fallback for embeddings
  public async searchSimilarContent(query: string, topK: number = 3): Promise<VectorSearchResult[]> {
    if (!this.initialized) {
      this.initializeVectorStore();
    }

    try {
      const keywords = query.toLowerCase().split(/\s+/);
      const results: VectorSearchResult[] = [];

      // Calculate simple keyword match scores
      for (const [_id, content] of this.contents.entries()) {
        const contentLower = content.toLowerCase();
        let score = 0;
        
        // Count keyword matches
        for (const keyword of keywords) {
          if (keyword.length < 3) continue; // Skip very short words
          
          // Count occurrences of the keyword
          const regex = new RegExp(keyword, 'gi');
          const matches = contentLower.match(regex);
          if (matches) {
            score += matches.length;
          }
        }
        
        // Add to results if there's any match
        if (score > 0) {
          results.push({ content, score });
        }
      }

      // Sort by score and return top K results
      return results
        .sort((a, b) => b.score - a.score)
        .slice(0, topK);
    } catch (error) {
      console.error('Failed to search similar content:', error);
      return [];
    }
  }
}

// Export singleton instance
export const vectorStoreService = new VectorStoreService();